import { useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill";

import "katex/dist/katex.min.css";
import "mathquill4quill/mathquill4quill.css";
import "react-quill/dist/quill.snow.css";

// IMPORTANT: expose katex globally (Quill needs this)
import katex from "katex";
window.katex = katex;

// mathquill4quill
import mathquill4quill from "mathquill4quill";

const modules = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ header: [1, 2, false] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    ["formula"], // 👈 math button
    ["clean"],
  ],
  formula: true,
};

const formats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
  "image",
  "formula",
];

/**
 * @param {string} [value] - When set, the editor is controlled (syncs to parent state).
 * @param {(html: string) => void} [onChange] - Fires with HTML on each change (editable mode).
 * @param {boolean} [readOnly]
 * @param {string} [placeholder]
 */
export default function MathEditor({
  value: valueProp,
  onChange,
  readOnly = false,
  placeholder = "Write something... (supports math + images)",
  style,
}) {
  const [internalValue, setInternalValue] = useState("");
  const isControlled = valueProp !== undefined;
  const value = isControlled ? valueProp : internalValue;
  const handleChange = (html) => {
    onChange?.(html);
    if (!isControlled) setInternalValue(html);
  };
  const quillRef = useRef(null);

  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const enableMathQuillFormulaAuthoring = mathquill4quill();

    enableMathQuillFormulaAuthoring(quill, {
      operators: [
        ["\\frac{x}{y}", "\\frac"],
        ["\\sqrt{x}", "\\sqrt"],
        ["x^2", "^"],
        ["x_n", "_"],
        ["\\sum", "\\sum"],
        ["\\int", "\\int"],
      ],
      displayHistory: true,
      historyCacheKey: "__math_history__",
    });
  }, []);
  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const handlePaste = (e) => {
      const clipboardData = e.clipboardData;
      const items = clipboardData?.items;
      if (!clipboardData || !items) return;

      // Word often puts both rich text (html/plain) AND an image snapshot in the clipboard.
      // If we intercept images unconditionally, we end up pasting the snapshot instead of text.
      const types = Array.from(clipboardData.types || []);
      const hasText = types.includes("text/plain") || types.includes("text/html");
      if (hasText) return;

      const imageItem = Array.from(items).find((item) =>
        item.type?.startsWith("image/")
      );
      if (!imageItem) return;

      e.preventDefault();

      const file = imageItem.getAsFile();
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target?.result;
        if (!base64) return;

        const range = quill.getSelection(true);
        quill.insertEmbed(range.index, "image", base64);
      };

      reader.readAsDataURL(file);
    };

    quill.root.addEventListener("paste", handlePaste);

    return () => {
      quill.root.removeEventListener("paste", handlePaste);
    };
  }, []);
  return (
    <div style={{ maxWidth: "100%", wrap: "wrap", minHeight: 100 }}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={handleChange}
        readOnly={readOnly}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        style={style}
      />
    </div>
  );
}
